import { Field } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useFluentMountNode } from '../hooks/useFluentMountNode';
import { useLocalizedDatepicker } from '../hooks/useLocalizedDatepicker';
import './LocalizedFormDatePicker.scss';

export interface LocalizedFormDatePickerProps {
  label?: string | { content: string; id?: string };
  selectedDate?: Date;
  onDateChange?: (event: React.SyntheticEvent | undefined, data: { value: Date | null }) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  /** When true, the date picker is non-interactive but retains normal (non-grayed-out) styling. */
  readOnly?: boolean;
  required?: boolean;
  errorMessage?: string;
  className?: string;
  restrictedDates?: Date[];
  /**
   * Visual appearance of the date input.
   * Defaults to "filled-darker" to match legacy Northstar look.
   **/
  appearance?: 'outline' | 'underline' | 'filled-darker' | 'filled-lighter';
  /** Accessible label for the date picker when no visible label is provided. */
  ariaLabel?: string;
  'data-cy'?: string;
}

/**
 * A wrapped Fluent UI DatePicker (v9 compat) which provides localized
 * strings automatically based on the given i18n-Provider.
 */
export const LocalizedFormDatePicker: FC<LocalizedFormDatePickerProps> = ({
  label,
  selectedDate,
  onDateChange,
  errorMessage,
  className,
  required,
  readOnly,
  restrictedDates,
  appearance = 'filled-darker',
  ariaLabel,
  ...rest
}) => {
  const { t } = useTranslation();
  const localizedStrings = useLocalizedDatepicker();

  const mountNode = useFluentMountNode();

  const labelText = typeof label === 'string' ? label : label?.content;

  const handleSelectDate = (date: Date | null | undefined) => {
    onDateChange?.(undefined, { value: date ?? null });
  };

  const formatDate = useCallback(
    (date?: Date) => {
      if (!date) return '';
      const day = date.getDate().toString().padStart(2, '0');
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      const monthName = localizedStrings?.months[monthIndex] ?? (monthIndex + 1).toString();
      return `${day}. ${monthName} ${year}`;
    },
    [localizedStrings],
  );

  const effectiveAriaLabel = labelText
    ? undefined
    : ariaLabel ??
      t('pc:localizedFormDatePicker:localizedFormDatePicker:date_picker_aria_label', {
        defaultValue: 'Datum auswählen',
      });

  const datePicker = (
    <DatePicker
      isMonthPickerVisible={false}
      data-cy="datePicker"
      value={selectedDate ?? null}
      onSelectDate={handleSelectDate}
      formatDate={formatDate}
      placeholder={localizedStrings?.inputPlaceholder}
      strings={localizedStrings}
      required={required}
      appearance={appearance}
      aria-label={effectiveAriaLabel}
      calendar={restrictedDates ? { restrictedDates } : undefined}
      mountNode={mountNode}
      popupSurface={{ className: 'aix-localized-form-date-picker' }}
      {...rest}
      {...(readOnly && { open: false, className: 'aix-date-picker--read-only' })}
    />
  );

  if (labelText) {
    return (
      <Field
        label={labelText}
        validationMessage={errorMessage}
        required={required}
        className={className}
      >
        {datePicker}
      </Field>
    );
  }

  if (className) {
    return <div className={className}>{datePicker}</div>;
  }

  return datePicker;
};
